import { Toaster as Sonner } from "sonner";
import { X } from "lucide-react";
import { type ComponentProps } from "react";

import { cn } from "@/lib/utils";

type ToastProps = ComponentProps<typeof Sonner>;

const TOAST_BASE = cn(
  `
    relative flex items-center gap-1.5 rounded-sm border
    border-(--console-border)
  `,
  "p-4 pr-10 text-(--console-foreground) shadow-lg backdrop-blur-[6px]",
  "bg-[color-mix(in_srgb,#fff_80%,transparent)]",
  `
    data-[type=success]:bg-[color-mix(in_oklch,oklch(96%_0.04_145deg)_85%,transparent)]
  `,
  "data-[type=success]:text-[oklch(22%_0.08_145deg)]",
  `
    data-[type=warning]:bg-(--feedback-warn-bg)
    data-[type=warning]:text-(--feedback-warn-color)
  `,
  `
    data-[type=info]:bg-(--feedback-info-bg)
    data-[type=info]:text-(--feedback-info-color)
  `,
  "has-data-[toast-progress=fill]:pb-4",
  "has-data-[toast-progress=fill]:**:data-description:text-inherit!",
);

export const TOAST_CLASSNAMES = {
  base: TOAST_BASE,
} as const;

/** Class for toast description when it shows the progress bar overlay. */
export const TOAST_DESCRIPTION_PROGRESS_CLASS = cn(
  "absolute inset-x-0 bottom-0 m-0 w-full min-w-0 text-inherit!",
);

const TOAST_CLOSE_BUTTON_CLASS = cn(
  "absolute top-1/2 right-2 left-auto flex size-6 min-w-0 shrink-0",
  `
    -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm
    border-0 p-0
  `,
  "bg-transparent! text-current! transition-colors",
  `
    hover:bg-[oklch(0%_0_0deg/6%)]!
    active:bg-[oklch(0%_0_0deg/6%)]!
  `,
  `
    focus-visible:shadow-[0_0_0_1px_var(--console-accent)]!
    focus-visible:outline-none!
  `,
);

export const Toast = ({ ...props }: ToastProps) => (
  <Sonner
    theme="system"
    className="group [display:inherit]"
    position="top-center"
    closeButton
    visibleToasts={2}
    richColors={false}
    icons={{
      close: <X className="size-3" aria-hidden />,
    }}
    toastOptions={{
      unstyled: true,
      classNames: {
        toast: TOAST_BASE,
        description: "text-foreground/45",
        actionButton: "bg-primary text-primary-foreground",
        cancelButton: "bg-black/5 text-foreground/45",
        closeButton: TOAST_CLOSE_BUTTON_CLASS,
      },
    }}
    {...props}
  />
);

export interface ToastProgressBarProps {
  duration: number;
}

export const ToastProgressBar = ({ duration }: ToastProgressBarProps) => (
  <div className="w-full text-inherit!">
    <div
      className="h-[3px] overflow-hidden rounded-b-lg bg-current/25"
      role="progressbar"
      aria-valuenow={0}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        data-toast-progress="fill"
        className="
          h-full animate-[toast-progress-shrink_linear_forwards] rounded-b-lg
          bg-current opacity-85
        "
        style={{ animationDuration: `${duration}ms` }}
      />
    </div>
  </div>
);
